// Windsurf generated file
import { useEffect, useState, useMemo, useCallback } from 'react'
import {
  getAllFeatureFlags,
  getFeatureFlag,
  FeatureFlags,
  FeatureFlagName,
  defaultFlags,
} from '../../api/feature-flags-api'
import { FeatureFlagsContext } from './feature-flags-context-definition'

interface FeatureFlagsProviderProps {
  readonly children: React.ReactNode
}

export function FeatureFlagsProvider({
  children,
}: Readonly<FeatureFlagsProviderProps>) {
  const [serverFlags, setServerFlags] = useState<FeatureFlags>(defaultFlags)
  const [localOverrides, setLocalOverrides] = useState<Map<string, boolean>>(
    new Map()
  )
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadFeatureFlags = async () => {
      try {
        const fetchedFlags = await getAllFeatureFlags()
        setServerFlags(fetchedFlags)
      } catch (error) {
        console.error('Error loading feature flags:', error)
        // Keep default flags on error
      } finally {
        setLoading(false)
      }
    }

    void loadFeatureFlags()
  }, [])

  // Merge server flags with local overrides
  const flags = useMemo((): FeatureFlags => {
    const merged = { ...serverFlags }
    localOverrides.forEach((value, key) => {
      if (key in merged) {
        merged[key as keyof FeatureFlags] = value
      }
    })
    return merged
  }, [serverFlags, localOverrides])

  const toggleLocalOverride = useCallback(
    (flagName: keyof FeatureFlags) => {
      setLocalOverrides((prev) => {
        const newOverrides = new Map(prev)
        if (newOverrides.has(flagName)) {
          // Remove override - revert to server value
          newOverrides.delete(flagName)
        } else {
          // Add override - toggle current effective value
          newOverrides.set(flagName, !flags[flagName])
        }
        return newOverrides
      })
    },
    [flags]
  )

  // Windsurf generated
  const toggleAllLocalOverrides = useCallback(() => {
    setLocalOverrides((prev) => {
      const newOverrides = new Map(prev)
      Object.keys(flags).forEach((flagName) => {
        const key = flagName as keyof FeatureFlags
        // Skip ENABLE_DEV_TOOLS to prevent accidental disabling in beta/stg1
        if (key === FeatureFlagName.ENABLE_DEV_TOOLS) return

        if (newOverrides.has(key)) {
          // Remove override - revert to server value
          newOverrides.delete(key)
        } else {
          // Add override - toggle current effective value
          newOverrides.set(key, !flags[key])
        }
      })
      return newOverrides
    })
  }, [flags])

  const clearLocalOverrides = useCallback(() => {
    setLocalOverrides(new Map())
  }, [])

  const pollFlag = useCallback(async (flagName: keyof FeatureFlags) => {
    try {
      const value = await getFeatureFlag(flagName)
      // Update server flags with fresh value
      setServerFlags((prev) => ({
        ...prev,
        [flagName]: value,
      }))
      // Remove local override so we see the server value
      setLocalOverrides((prev) => {
        const newOverrides = new Map(prev)
        newOverrides.delete(flagName)
        return newOverrides
      })
    } catch (error) {
      console.error(`Error polling feature flag ${flagName}:`, error)
    }
  }, [])

  const featureFlags = useMemo(
    () => ({
      flags,
      loading,
      localOverrides,
      toggleLocalOverride,
      toggleAllLocalOverrides,
      clearLocalOverrides,
      pollFlag,
    }),
    [
      flags,
      loading,
      localOverrides,
      toggleLocalOverride,
      toggleAllLocalOverrides,
      clearLocalOverrides,
      pollFlag,
    ]
  )

  return (
    <FeatureFlagsContext value={featureFlags}>{children}</FeatureFlagsContext>
  )
}
